import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { acceptFedexAgreement } from "./schema/accept-fedex-agreement";
import { acceptMerchantCarrierFeeTerms } from "./schema/accept-merchant-carrier-fee-terms";
import { acceptPersonalizedSlipsTerms } from "./schema/accept-personalized-slips-terms";
import { acceptUpsAgreement } from "./schema/accept-ups-agreement";
import { acknowledgeBuyLabelErrors } from "./schema/acknowledge-buy-label-errors";
import { addCycleCountProducts } from "./schema/add-cycle-count-products";
import { advanceWorkOrder } from "./schema/advance-work-order";
import {
  deleteAllowedProblemPickPolicy,
  getAllowedProblemPickPolicies,
  upsertAllowedProblemPickPolicy,
} from "./schema/allowed-problem-pick-policy";
import { assignAllUnassignedItemsToTote } from "./schema/assign-all-unassigned-items-to-tote";
import { assignItemToTote } from "./schema/assign-item-to-tote";
import { assignWorkOrderTote } from "./schema/assign-work-order-tote";
import { autoMergeSpecifiedGroups } from "./schema/auto-merge-specified-groups";
import {
  cancelBinAssignmentSession,
  completeBinAssignmentSession,
  createBinAssignmentSession,
  getActiveBinAssignmentSessions,
  getBinAssignmentSession,
  removeBinAssignmentProduct,
  upsertBinAssignmentProduct,
} from "./schema/bin-assignment-session";
import { bulkAdvancePlacing } from "./schema/bulk-advance-placing";
import { bulkAdvanceWorkOrders } from "./schema/bulk-advance-work-orders";
import { bulkAssignWorkOrderTotes } from "./schema/bulk-assign-work-order-totes";
import { bulkMarkUnableToPick } from "./schema/bulk-mark-unable-to-pick";
import {
  cancelBulkPickSession,
  completeBulkPickSession,
  createBulkPickSession,
  getActiveBulkPickSession,
  releaseBulkPickSession,
  takeOverBulkPickSession,
} from "./schema/bulk-pick-session";
import { bulkPickWorkOrderLines } from "./schema/bulk-pick-work-order-lines";
import { buyBulkShipmentLabels } from "./schema/buy-bulk-shipment-labels";
import { buyLabels } from "./schema/buy-labels";
import { buyPriorityFilterLabels } from "./schema/buy-priority-filter-labels";
import { buyReturnLabel } from "./schema/buy-return-label";
import { cancelFreightBooking } from "./schema/cancel-freight-booking";
import { cancelFulfillmentGroups } from "./schema/cancel-fulfillment-group";
import { cancelManualFulfillments } from "./schema/cancel-manual-fulfillments";
import { cancelOrderItems } from "./schema/cancel-order-items";
import { cancelPickSession } from "./schema/cancel-pick-session";
import { cancelPicking } from "./schema/cancel-picking";
import { cancelPrintJobs } from "./schema/cancel-print-jobs";
import { cancelWorkOrder } from "./schema/cancel-work-order";
import { changePickSessionItemBin } from "./schema/change-pick-session-item-bin";
import { checkPrintLabelInventory } from "./schema/check-print-label-inventory";
import { claimAndStartTask } from "./schema/claim-and-start-task";
import { claimWorkOrderStep } from "./schema/claim-work-order-step";
import { completeCycleCount } from "./schema/complete-cycle-count";
import { completePickSessionSourcing } from "./schema/complete-pick-session-sourcing";
import { completeShipmentReceiving } from "./schema/complete-shipment-receiving";
import { confirmDirectBinCount } from "./schema/confirm-direct-bin-count";
import { createConsolidationBins } from "./schema/consolidation-bins/create-consolidation-bin";
import { deleteConsolidationBins } from "./schema/consolidation-bins/delete-consolidation-bins";
import { getConsolidationBinsOnHand } from "./schema/consolidation-bins/get-consolidation-bins-on-hand";
import { getMerchantGradeOptions } from "./schema/consolidation-bins/get-merchant-grade-options";
import { listConsolidationBins } from "./schema/consolidation-bins/list-consolidation-bins";
import { printConsolidationBinLabels } from "./schema/consolidation-bins/print-consolidation-bin-labels";
import { searchMerchantSkus } from "./schema/consolidation-bins/search-merchant-skus";
import { updateConsolidationBinCriteria } from "./schema/consolidation-bins/update-consolidation-bin-criteria";
import { createConsolidationGroup } from "./schema/consolidation-settings/create-consolidation-group";
import { createConsolidationSchedule } from "./schema/consolidation-settings/create-consolidation-schedule";
import { deleteConsolidationGroup } from "./schema/consolidation-settings/delete-consolidation-group";
import { getConsolidationSettings } from "./schema/consolidation-settings/get-consolidation-settings";
import { updateConsolidationGroup } from "./schema/consolidation-settings/update-consolidation-group";
import { updateConsolidationSchedule } from "./schema/consolidation-settings/update-consolidation-schedule";
import { updateConsolidationScheduleAssignments } from "./schema/consolidation-settings/update-consolidation-schedule-assignments";
import {
  closeConsolidation,
  fulfillConsolidation,
  getConsolidationFilterOptions,
  getConsolidationStatusCounts,
  listConsolidations,
  sealConsolidation,
} from "./schema/consolidation-summary";
import { createBalanceTransactionExport } from "./schema/create-balance-transaction-export";
import { createInventoryLevelLedgerExport } from "./schema/create-inventory-level-ledger-export";
import { getConsolidationCartonReturnsCenters } from "./schema/get-consolidation-carton-returns-centers";
import { getLabelPrebought } from "./schema/get-label-prebought";
import { cancelItemWorkRequest } from "./schema/item-work-requests/cancel-item-work-request";
import { listAssignedCycleCounts } from "./schema/list-assigned-cycle-counts";
import { prepareConsolidationCartonLabel } from "./schema/prepare-consolidation-carton-label";
import { printConsolidationCartonShippingLabel } from "./schema/print-consolidation-carton-shipping-label";
import { purchaseConsolidationCartonLabel } from "./schema/purchase-consolidation-carton-label";
import { quoteConsolidationCartonLabel } from "./schema/quote-consolidation-carton-label";
import {
  getReplenishmentQueue,
  startNextReplenishmentTask,
} from "./schema/replenishment-queue";
import { saveConsolidationCartonLabelDraft } from "./schema/save-consolidation-carton-label-draft";

import { createConsolidationFulfillmentGroup } from "./schema/create-consolidation-fulfillment-group";
import { createCycleCount } from "./schema/create-cycle-count";
import { createDefaultBucketShippingMethods } from "./schema/create-default-bucket-shipping-methods";
import { createEodManifestForAll } from "./schema/create-eod-manifest-for-all";
import { createEodManifestForIds } from "./schema/create-eod-manifest-for-ids";
import { createManualFulfillmentGroups } from "./schema/create-manual-fulfillment-groups";
import { createOmsPriorityFilter } from "./schema/create-oms-priority-filter";
import { createParcel } from "./schema/create-parcel";
import { createParcels } from "./schema/create-parcels";
import { createPickSession } from "./schema/create-pick-session";
import { createPickSessionFilter } from "./schema/create-pick-session-filter";
import { createPickSessionPriorityFilter } from "./schema/create-pick-session-priority-filter";
import { createPriorityBomPickSession } from "./schema/create-priority-bom-pick-session";
import { createReturnAddress } from "./schema/create-return-address";
import { createReturnLabel } from "./schema/create-return-label";
import { createShippingMethod } from "./schema/create-shipping-method";
import { createShipStationApiIntegration } from "./schema/create-shipstation-api-integration";
import { createStorageLocations } from "./schema/create-storage-location";
import { createTaxIdentifier } from "./schema/create-tax-identifier";
import { createViewExport } from "./schema/create-view-export";
import { createWorkOrder } from "./schema/create-work-order";
import { createWorkOrderPriorityFilter } from "./schema/create-work-order-priority-filter";
import { createWorkOrderStation } from "./schema/create-work-order-station";
import { getAddressAutoUpdatedCount } from "./schema/dashboard/get-address-auto-updated-count";
import { getAovByChannel } from "./schema/dashboard/get-aov-by-channel";
import { getAverageFulfillmentPerHour } from "./schema/dashboard/get-average-fulfillment-per-hour";
import { getAverageLabelPrice } from "./schema/dashboard/get-average-label-price";
import { getAvgAdjustmentByCarrier } from "./schema/dashboard/get-avg-adjustment-by-carrier";
import { getAvgClaimValueByCarrier } from "./schema/dashboard/get-avg-claim-value-by-carrier";
import { getAvgDaysToFulfillOverTime } from "./schema/dashboard/get-avg-days-to-fulfill-over-time";
import { getAvgLabelCostByCarrier } from "./schema/dashboard/get-avg-label-cost-by-carrier";
import { getAvgShippingRevenuePerOrder } from "./schema/dashboard/get-avg-shipping-revenue-per-order";
import { getAvgSurcharge } from "./schema/dashboard/get-avg-surcharge";
import { getAvgTimeToDeliverByCarrier } from "./schema/dashboard/get-avg-time-to-deliver-by-carrier";
import { getAvgTimeToDeliverByServiceLevel } from "./schema/dashboard/get-avg-time-to-deliver-by-service-level";
import { getAvgTimeToResolutionByCarrier } from "./schema/dashboard/get-avg-time-to-resolution-by-carrier";
import { getCanonicalLocations } from "./schema/dashboard/get-canonical-locations";
import { getCarrierDeliveryErrorsOverTime } from "./schema/dashboard/get-carrier-delivery-errors-over-time";
import { getCarrierSpendOverTime } from "./schema/dashboard/get-carrier-spend-over-time";
import { getCarrierSurchargesOverTime } from "./schema/dashboard/get-carrier-surcharges-over-time";
import { getCarrierVolumeOverTime } from "./schema/dashboard/get-carrier-volume-over-time";
import { getClaimApprovalRateOverTimeByCarrier } from "./schema/dashboard/get-claim-approval-rate-over-time-by-carrier";
import { getClaimsByCarrier } from "./schema/dashboard/get-claims-by-carrier";
import { getClaimsByReason } from "./schema/dashboard/get-claims-by-reason";
import { getClaimsMetrics } from "./schema/dashboard/get-claims-metrics";
import { getClaimsRevenueRecoveredOverTime } from "./schema/dashboard/get-claims-revenue-recovered-over-time";
import { getCostPerOunceShipped } from "./schema/dashboard/get-cost-per-ounce-shipped";
import { getDashboardSlaViews } from "./schema/dashboard/get-dashboard-sla-views";
import { getErrorFulfillmentBreakdown } from "./schema/dashboard/get-error-fulfillment-breakdown";
import { getEventTypeCount } from "./schema/dashboard/get-event-type-count";
import { getFulfillmentsMetrics } from "./schema/dashboard/get-fulfillments-metrics";
import { getFulfillmentsOverTime } from "./schema/dashboard/get-fulfillments-over-time";
import { getLostOrdersCount } from "./schema/dashboard/get-lost-orders-count";
import { deleteStorageLocations } from "./schema/delete-storage-locations";
import { getLotProducts, getLots, getLotsCount } from "./schema/get-lots";
import { createItemWorkRequest } from "./schema/item-work-requests/create-item-work-request";
import {
  completeItemWorkRequest,
  deleteItemPhotoCapture,
  getItemPhotoRequestForItem,
  getItemWorkRequestPhotos,
  uploadItemPhotoCapture,
} from "./schema/item-work-requests/item-photo-request";
import {
  deletePhotoInstructions,
  listPhotoInstructions,
  savePhotoInstructions,
} from "./schema/item-work-requests/photo-instructions";
import { rejectItemWorkRequest } from "./schema/item-work-requests/reject-item-work-request";
import {
  getItemWorkRequestsCount,
  searchItemWorkRequests,
} from "./schema/item-work-requests/search-item-work-requests";
import { createLot, listLots, updateLot } from "./schema/lots";
import {
  createManualConsolidation,
  getManualConsolidationOptions,
} from "./schema/manual-consolidation";

import { getMergedOrderSavings } from "./schema/dashboard/get-merged-order-savings";
import { getOmsInsights } from "./schema/dashboard/get-oms-insights";
import { getOnTimeDeliveryRate } from "./schema/dashboard/get-on-time-delivery-rate";
import { getOnTimeFulfillmentRate } from "./schema/dashboard/get-on-time-fulfillment-rate";
import { getOrderToShipTime } from "./schema/dashboard/get-order-to-ship-time";
import { getOrdersByChannel } from "./schema/dashboard/get-orders-by-channel";
import { getOrdersMetrics } from "./schema/dashboard/get-orders-metrics";
import { getPackingErrorRate } from "./schema/dashboard/get-packing-error-rate";
import { getPickToPackTime } from "./schema/dashboard/get-pick-to-pack-time";
import { resetDashboardSlaView } from "./schema/dashboard/reset-dashboard-sla-view";
import { saveDashboardSlaView } from "./schema/dashboard/save-dashboard-sla-view";

import { addOutboundFunds } from "@redotech/redo-fulfillment-outbound-labels-common/rpc/add-outbound-funds";
import {
  createConsolidationPackingCarton,
  getConsolidationCarton,
  getConsolidationCartons,
  getConsolidationMerchantPackingSession,
  getConsolidationPackingCartonLabel,
  getConsolidationPackingSummary,
  moveConsolidationPackingUnit,
  packConsolidationInventoryItem,
  putAwayInventoryItemIntoConsolidationCarton,
  removeConsolidationPackingCarton,
  sealConsolidationPacking,
  setConsolidationCartonParcel,
  setConsolidationPackingPlan,
  unpackConsolidationPackingUnit,
} from "@redotech/redo-merchant-fulfillment-rpc-packing-station/packing-station";
import { addProductToStorageLocation } from "./schema/add-product-to-storage-location";
import { adjustInventoryLevel } from "./schema/adjust-inventory-level";
import { assignFulfillmentGroupToMerchant } from "./schema/assign-to-merchant";
import { getSalesVsRefundsOverTime } from "./schema/dashboard/get-sales-vs-refunds-over-time";
import { getServiceLevelShippingMarginOverTime } from "./schema/dashboard/get-service-level-shipping-margin-over-time";
import { getServiceLevelVolumeOverTime } from "./schema/dashboard/get-service-level-volume-over-time";
import { getShipmentErrors } from "./schema/dashboard/get-shipment-errors";
import { getShipmentVolumeByPackage } from "./schema/dashboard/get-shipment-volume-by-package";
import { getShipmentsByCarrier } from "./schema/dashboard/get-shipments-by-carrier";
import { getShippingErrorRateByCarrier } from "./schema/dashboard/get-shipping-error-rate-by-carrier";
import { getShippingMarginOverTime } from "./schema/dashboard/get-shipping-margin-over-time";
import { getShippingMetrics } from "./schema/dashboard/get-shipping-metrics";
import { getSpendByCarrier } from "./schema/dashboard/get-spend-by-carrier";
import { getSplitOrderCount } from "./schema/dashboard/get-split-order-count";
import { getSplitOrderRate } from "./schema/dashboard/get-split-order-rate";
import { getSurchargeByCategory } from "./schema/dashboard/get-surcharge-by-category";
import { getTopDenialReasons } from "./schema/dashboard/get-top-denial-reasons";
import { getTopShippingErrors } from "./schema/dashboard/get-top-shipping-errors";
import { getTotalAdjustmentByCarrier } from "./schema/dashboard/get-total-adjustment-by-carrier";
import { getTotalLabelCost } from "./schema/dashboard/get-total-label-cost";
import { getTotalOrdersOverTime } from "./schema/dashboard/get-total-orders-over-time";
import { getTotalProductsShipped } from "./schema/dashboard/get-total-products-shipped";
import { getTotalRevenueShipped } from "./schema/dashboard/get-total-revenue-shipped";
import { getTotalShipments } from "./schema/dashboard/get-total-shipments";
import { getUniqueCarrierCount } from "./schema/dashboard/get-unique-carrier-count";
import { getVolumeByLocation } from "./schema/dashboard/get-volume-by-location";
import { getWorkOrderItemsCompleted } from "./schema/dashboard/get-work-order-items-completed";
import { getWorkOrderItemsCompletedByProduct } from "./schema/dashboard/get-work-order-items-completed-by-product";
import { updateDashboardMetricsSettings } from "./schema/dashboard/update-dashboard-metrics-settings";
import { declarePlacingLost } from "./schema/declare-placing-lost";
import { deleteCycleCounts } from "./schema/delete-cycle-counts";
import { deleteFulfillmentGroupView } from "./schema/delete-fulfillment-group-view";
import { deleteOmsPriorityFilter } from "./schema/delete-oms-priority-filter";
import { deleteParcel } from "./schema/delete-parcel";
import { deletePickSessionFilter } from "./schema/delete-pick-session-filter";
import { deletePickSessionPriorityFilter } from "./schema/delete-pick-session-priority-filter";
import { deleteShippingMethod } from "./schema/delete-shipping-method";
import { deleteTaxIdentifiers } from "./schema/delete-tax-identifiers";
import { deleteTotes } from "./schema/delete-totes";
import { deleteWorkOrderPriorityFilter } from "./schema/delete-work-order-priority-filter";
import { deleteWorkOrderStation } from "./schema/delete-work-order-station";
import { dismissPrintJobs } from "./schema/dismiss-print-jobs";
import { dissociateStorageLocationInventory } from "./schema/dissociate-storage-location-inventory";
import { duplicateShipments } from "./schema/duplicate-shipments";
import { editManualFulfillmentGroups } from "./schema/edit-manual-fulfillment-groups";
import { enableAutoMerge } from "./schema/enable-auto-merge";
import { enqueuePrintJobs } from "./schema/enqueue-print-jobs";
import { executeInstantTransfer } from "./schema/execute-instant-transfer";
import { exportEnrichedBalanceTransactions } from "./schema/export-enriched-balance-transactions";
import { fulfillBinsAsManualGroup } from "./schema/fulfill-bins-as-manual-group";
import { fulfillFulfillmentGroupWithFreightBooking } from "./schema/fulfill-fulfillment-group-with-freight-booking";
import { getFulfillmentServiceForTeam } from "./schema/fulfillment-service/get-fulfillment-service-for-team";
import { getFulfillmentServicesThatFulfillForTeam } from "./schema/fulfillment-service/get-fulfillment-services-that-fulfill-for-team";
import { generateBomInputDemand } from "./schema/generate-bom-input-demand";
import { generateWorkOrderBarcodeLabels } from "./schema/generate-work-order-barcode-labels";
import { generateWorkOrderGenerationReport } from "./schema/generate-work-order-generation-report";
import { getActiveUserPickSessions } from "./schema/get-active-pick-sessions";
import { getActiveUserTransferSessions } from "./schema/get-active-transfer-sessions";
import { getAddressesFromName } from "./schema/get-address-from-name";
import { getAutoMergeProgress } from "./schema/get-auto-merge-progress";
import { getAvailableBinsForFulfillmentGroups } from "./schema/get-available-bins-for-fulfillment-groups";
import { getAvailableBinsForUnpickedItems } from "./schema/get-available-bins-for-unpicked-items";
import { getBadWarehouseBins } from "./schema/get-bad-warehouse-bins";
import { getBalanceTransactionCount } from "./schema/get-balance-transaction-count";
import { getBalanceTransactionData } from "./schema/get-balance-transaction-data";
import { getBatchHistory } from "./schema/get-batch-history";
import { getBatchHistoryCount } from "./schema/get-batch-history-count";
import { getBatchSuggestionByKey } from "./schema/get-batch-suggestion-by-key";
import { getBatchSuggestions } from "./schema/get-batch-suggestions";
import { getBatchSuggestionsByKeys } from "./schema/get-batch-suggestions-by-keys";
import { getBinInventory } from "./schema/get-bin-inventory";
import { getBinInventoryForFulfillment } from "./schema/get-bin-inventory-for-fulfillment";
import { getBinsForProduct } from "./schema/get-bins-for-product";
import { getBinsForProductFamilyVariants } from "./schema/get-bins-for-product-family-variants";
import { getBinsForProducts } from "./schema/get-bins-for-products";
import { getBoms } from "./schema/get-boms";
import { getBulkFulfillmentView } from "./schema/get-bulk-fulfillment-view";
import { getBulkOneClickPrintFulfillmentGroupIds } from "./schema/get-bulk-one-click-print-fulfillment-group-ids";
import { getBulkOneClickPrintSummary } from "./schema/get-bulk-one-click-print-summary";
import { getBulkPickLines } from "./schema/get-bulk-pick-lines";
import { getCarrierParcels } from "./schema/get-carrier-parcels";
import { getClosedOrderToteReturn } from "./schema/get-closed-order-tote-return";
import { getConsolidationCommercialInvoiceCsv } from "./schema/get-consolidation-commercial-invoice-csv";
import { getConsolidationCommercialInvoicePdf } from "./schema/get-consolidation-commercial-invoice-pdf";
import { getConsolidationInventoryItems } from "./schema/get-consolidation-inventory-items";
import { getConsolidationInvoiceCsv } from "./schema/get-consolidation-invoice-csv";
import { getConsolidationInvoicePdf } from "./schema/get-consolidation-invoice-pdf";
import { getConsolidationManifestCsv } from "./schema/get-consolidation-manifest-csv";
import { getConsolidationManifestPdf } from "./schema/get-consolidation-manifest-pdf";
import { getCrdbLocations } from "./schema/get-crdb-locations";
import { getCycleCount } from "./schema/get-cycle-count";
import { getEligibleBinCount } from "./schema/get-eligible-bin-count";
import { getEndOfDayManifests } from "./schema/get-end-of-day-manifests";
import { getFreeShippingCodes } from "./schema/get-free-shipping-codes";
import { getFulfillmentBacksyncProgress } from "./schema/get-fulfillment-backsync-progress";
import { getFulfillmentGroupMerchantTeamIds } from "./schema/get-fulfillment-group-merchant-team-ids";
import { getFulfillmentGroupShoppedServices } from "./schema/get-fulfillment-group-shopped-services";
import { getFulfillmentGroupSummaries } from "./schema/get-fulfillment-group-summaries";
import { getFulfillmentGroupSummaryCount } from "./schema/get-fulfillment-group-summary-count";
import { getFulfillmentGroupUniqueValues } from "./schema/get-fulfillment-group-unique-values";
import { getFulfillmentGroupsByOrderId } from "./schema/get-fulfillment-groups-by-order-id";
import { getFulfillmentGroupsForOrder } from "./schema/get-fulfillment-groups-for-order";
import { getGraphicBlanks } from "./schema/get-graphic-blanks";
import { getHandoffByToteBarcode } from "./schema/get-handoff-by-tote-barcode";
import {
  getInventoryLevelLedger,
  getInventoryLevelLedgerCount,
  getInventoryLevelLedgerUniqueValues,
} from "./schema/get-inventory-level-ledger";
import { getLatestOpenFulfillmentGroup } from "./schema/get-latest-open-fulfillment-group";
import { getManagedMerchants } from "./schema/get-managed-merchants";
import { getManifestableGroupsCounts } from "./schema/get-manifestable-group-counts";
import {
  getMergeSuggestionGroups,
  getMergeSuggestionSummaries,
} from "./schema/get-merge-suggestion-summaries";
import { getMergeSuggestions } from "./schema/get-merge-suggestions";
import { getMergeSuggestionsForGroup } from "./schema/get-merge-suggestions-for-group";
import { getNonPickedProductIds } from "./schema/get-non-picked-product-ids";
import { getOmsAutoMergeOrderExamples } from "./schema/get-oms-auto-merge-order-examples";
import { getOmsAutoMergeSavings } from "./schema/get-oms-auto-merge-savings";
import { getOmsPriorityFilters } from "./schema/get-oms-priority-filters";
import { getOmsReturnLabelRates } from "./schema/get-oms-return-label-rates";
import { getOnboardingProgress } from "./schema/get-onboarding-progress";
import { getOptimizedBatchFulfillmentGroupIds } from "./schema/get-optimized-batch-fulfillment-group-ids";
import { getOriginAddresses } from "./schema/get-origin-addresses";
import { getOutboundBalance } from "./schema/get-outbound-balance";
import { getOutboundInvoice } from "./schema/get-outbound-invoice";
import { getOutboundInvoices } from "./schema/get-outbound-invoices";
import { getOutboundLabelsMetrics } from "./schema/get-outbound-labels-metrics";
import { getOverboxConfig } from "./schema/get-overbox-config";
import { getOverboxLeg } from "./schema/get-overbox-leg";
import { getPackingSlipPredictions } from "./schema/get-packing-slip-predictions";
import { getPackingSlipWeatherWarnings } from "./schema/get-packing-slip-weather-warnings";
import { getParcelAutomationReferences } from "./schema/get-parcel-automation-references";
import { getParcelBoxLabel } from "./schema/get-parcel-box-label";
import { getParcelPackingSlip } from "./schema/get-parcel-packing-slip";
import { getParcels } from "./schema/get-parcels";
import { getPickLinesForWorkOrders } from "./schema/get-pick-lines-for-work-orders";
import { getPickListCsv } from "./schema/get-pick-list-csv";
import { getPickSession } from "./schema/get-pick-session";
import { getPickSessionFillCandidates } from "./schema/get-pick-session-fill-candidates";
import { getPickSessionFilters } from "./schema/get-pick-session-filters";
import { getPickSessionLotRequirements } from "./schema/get-pick-session-lot-requirements";
import { getPickSessionPriorityFilters } from "./schema/get-pick-session-priority-filters";
import { getPickSessionRoute } from "./schema/get-pick-session-route";
import { getPlacingBatchesByTote } from "./schema/get-placing-batches-by-tote";
import { getPlacingWorkOrdersByTote } from "./schema/get-placing-work-orders-by-tote";
import { getPossibleReturnsForProblemOrder } from "./schema/get-possible-returns-for-problem-order";
import { getPrintQueueStatus } from "./schema/get-print-queue-status";
import { getPrintNodeInfo } from "./schema/get-printnode-info";
import { getProductFamilyVariants } from "./schema/get-product-family-variants";
import { getProductInventoryInBin } from "./schema/get-product-inventory-in-bin";
import { getProductivityDashboardData } from "./schema/get-productivity-dashboard-data";
import { getProductsWithoutBinMetafield } from "./schema/get-products-without-bin-metafield";
import { getReassignmentLocations } from "./schema/get-reassignment-locations";
import { getCarrierServiceCombinations } from "./schema/get-redo-carrier-services-for-merchant";
import { getRefreshTeamRatesProgress } from "./schema/get-refresh-team-rates-progress";
import { getRelevantFulfillmentGroupData } from "./schema/get-relevant-fulfillment-group-data";
import { getReplacementGroups } from "./schema/get-replacement-groups";
import { getRerunAutomationsProgress } from "./schema/get-rerun-automations-progress";
import { getReturnAddresses } from "./schema/get-return-addresses";
import { getScanVerifyPopups } from "./schema/get-scan-verify-popups";
import { getShippedItemsReportData } from "./schema/get-shipped-items-report-data";
import { getShipStationApiIntegration } from "./schema/get-shipstation-api-integration";
import { getSiblingBatches } from "./schema/get-sibling-batches";
import { getStationQueue } from "./schema/get-station-queue";
import { getStorageLocationById } from "./schema/get-storage-location-by-id";
import { getStorageLocationByName } from "./schema/get-storage-location-by-name";
import { getStorageLocationFilterLocations } from "./schema/get-storage-location-filter-locations";
import { getStorageLocationPrefixGroup } from "./schema/get-storage-location-prefix-group";
import { getStorageLocationUniqueValues } from "./schema/get-storage-location-unique-values";
import { getStorageLocations } from "./schema/get-storage-locations";
import { getStorageLocationsCount } from "./schema/get-storage-locations-count";
import { getStorageLocationsForTransfer } from "./schema/get-storage-locations-for-transfer";
import { getSuggestedBinsForFulfillmentGroup } from "./schema/get-suggested-bins-for-fulfillment-group";
import { getSuggestedTransferDestinations } from "./schema/get-suggested-transfer-destinations";
import { getTaxIdentifiers } from "./schema/get-tax-identifiers";
import { getTeamCarrierServiceOptions } from "./schema/get-team-carrier-service-options";
import { getTransferGuidance } from "./schema/get-transfer-guidance";
import { getTransferLineMerchantTeamIds } from "./schema/get-transfer-line-merchant-team-ids";
import { getUnplacedShipmentReceipt } from "./schema/get-unplaced-shipment-receipt";
import { getUpsGhostAccountIntegration } from "./schema/get-ups-ghost-account-integration";
import { getWorkOrder } from "./schema/get-work-order";
import { getWorkOrderBatch } from "./schema/get-work-order-batch";
import { getWorkOrderBomNames } from "./schema/get-work-order-bom-names";
import { getWorkOrderBomSkus } from "./schema/get-work-order-bom-skus";
import { getWorkOrderInputProductNames } from "./schema/get-work-order-input-product-names";
import { getWorkOrderPriorityFilters } from "./schema/get-work-order-priority-filters";
import { getWorkOrderStationV2Enabled } from "./schema/get-work-order-station-v2-enabled";
import { getWorkOrderStations } from "./schema/get-work-order-stations";
import { getWorkOrderStepTitles } from "./schema/get-work-order-step-titles";
import { getWorkOrders } from "./schema/get-work-orders";
import { getWorkOrdersCount } from "./schema/get-work-orders-count";
import { hasCarrierServices } from "./schema/has-carrier-services";
import { hasLotTrackedProducts } from "./schema/has-lot-tracked-products";
import { healPickSessionSourcingDrift } from "./schema/heal-pick-session-sourcing-drift";
import { getInvoiceCsv } from "./schema/invoices/get-invoice-csv";
import { listAvailableHandoffsAtZone } from "./schema/list-available-handoffs-at-zone";
import { listConsolidationDocuments } from "./schema/list-consolidation-documents";
import { listConsolidationInventory } from "./schema/list-consolidation-inventory";
import { listCycleCounts } from "./schema/list-cycle-counts";
import { listOverboxLegs } from "./schema/list-overbox-legs";
import { listReceivableShipments } from "./schema/list-receivable-shipments";
import { logPrintJobProcessing } from "./schema/log-print-job-processing";
import { logPrintQueue } from "./schema/log-print-queue";
import { lookupOrder } from "./schema/lookup-order";
import { lookupProductByBarcode } from "./schema/lookup-product-by-barcode";
import { lookupWorkOrderByInventoryItem } from "./schema/lookup-work-order-by-inventory-item";
import { lookupWorkOrdersByTote } from "./schema/lookup-work-orders-by-tote";
import { markAsFulfilledWithData } from "./schema/mark-as-fulfilled-with-data";
import { markAsPrinted } from "./schema/mark-as-printed";
import { markFulfillmentGroupAsProblem } from "./schema/mark-fulfillment-group-as-problem";
import { markFulfillmentGroupsAsProblemPick } from "./schema/mark-fulfillment-groups-as-problem-pick";
import { markPickSessionAsPicked } from "./schema/mark-pick-session-as-picked";
import { markPickSessionItemInStock } from "./schema/mark-pick-session-item-in-stock";
import { markPickSessionItemOutOfStock } from "./schema/mark-pick-session-item-out-of-stock";
import { markAsPickedUp } from "./schema/mark-picked-up";
import { markAsReadyForPickup } from "./schema/mark-ready-for-pickup";
import { markToteAsFull } from "./schema/mark-tote-as-full";
import { markUnableToPick } from "./schema/mark-unable-to-pick";
import { addMerchantDashboardWidget } from "./schema/merchant-dashboards/add-merchant-dashboard-widget";
import { createMerchantDashboard } from "./schema/merchant-dashboards/create-merchant-dashboard";
import { deleteMerchantDashboard } from "./schema/merchant-dashboards/delete-merchant-dashboard";
import { getMerchantDashboard } from "./schema/merchant-dashboards/get-merchant-dashboard";
import { getOrCreateDefaultMerchantDashboard } from "./schema/merchant-dashboards/get-or-create-default-merchant-dashboard";
import { listMerchantDashboards } from "./schema/merchant-dashboards/list-merchant-dashboards";
import { removeMerchantDashboardWidget } from "./schema/merchant-dashboards/remove-merchant-dashboard-widget";
import { reorderMerchantDashboards } from "./schema/merchant-dashboards/reorder-merchant-dashboards";
import { updateMerchantDashboard } from "./schema/merchant-dashboards/update-merchant-dashboard";
import { updateMerchantDashboardWidget } from "./schema/merchant-dashboards/update-merchant-dashboard-widget";
import { mergeFulfillmentGroups } from "./schema/merge-fulfillment-groups";
import { mergeWorkOrders } from "./schema/merge-work-orders";
import {
  getMintsoftCourierConnection,
  setMintsoftCourierConnection,
} from "./schema/mintsoft-courier-connection";
import { moveFulfillmentGroupToTote } from "./schema/move-fulfillment-group-to-tote";
import { moveFulfillmentGroupToTotes } from "./schema/move-fulfillment-group-to-totes";
import { moveFulfillmentGroups } from "./schema/move-fulfillment-groups";
import { notifyMarketplace } from "./schema/notify-marketplace";
import { overrideNotReceivedGroups } from "./schema/override-not-received-groups";
import { deleteParcelPhoto } from "./schema/parcel-photos/delete-parcel-photo";
import { getParcelPhotos } from "./schema/parcel-photos/get-parcel-photos";
import { recordParcelPhotoBypass } from "./schema/parcel-photos/record-parcel-photo-bypass";
import { uploadParcelPhoto } from "./schema/parcel-photos/upload-parcel-photo";
import { pickAndAssignItemToTote } from "./schema/pick-and-assign-item-to-tote";
import { pickWorkOrderLine } from "./schema/pick-work-order-line";
import { placeHolds } from "./schema/place-holds";
import { getCurrentPoints } from "./schema/points/get-current-points";
import { getPointsTransactions } from "./schema/points/get-points-transactions";
import { getPointsTransactionsCount } from "./schema/points/get-points-transactions-count";
import { createPreset } from "./schema/presets/create-preset";
import { deletePreset } from "./schema/presets/delete-preset";
import { getPresets } from "./schema/presets/get-presets";
import { updatePreset } from "./schema/presets/update-preset";
import { previewToteHandoffs } from "./schema/preview-tote-handoffs";
import { previewWorkOrder } from "./schema/preview-work-order";
import { printBarcodeForProduct } from "./schema/print-barcode-for-product";
import { printBillOfLading } from "./schema/print-bill-of-lading";
import { printBinLabels } from "./schema/print-bin-labels";
import { printCommercialInvoices } from "./schema/print-commercial-invoice";
import { printLabels } from "./schema/print-labels";
import { printLocalPickUpLabels } from "./schema/print-local-pick-up-labels";
import { printLocationLabels } from "./schema/print-location-labels";
import { createPrintNodeAccount } from "./schema/print-node/create-printnode-account";
import { dismissPrintEngineRecommendation } from "./schema/print-node/dismiss-print-engine-recommendation";
import { signInPrintNodeAccount } from "./schema/print-node/sign-in-printnode-account";
import { updatePrintNodePassword } from "./schema/print-node/update-printnode-password";
import { printOrderSummaryReport } from "./schema/print-order-summary-report";
import { printPackingSlip } from "./schema/print-packing-slip";
import { printParcelPackingSlips } from "./schema/print-parcel-packing-slips";
import { printPersonalizedSlip } from "./schema/print-personalized-slip";
import { printPickList } from "./schema/print-pick-list";
import { printPickSessions } from "./schema/print-pick-sessions";
import { printReturnLabels } from "./schema/print-return-labels";
import { printShortcutBarcodes } from "./schema/print-shortcut-barcodes";
import { printStorageLocationLabels } from "./schema/print-storage-location-labels";
import { printTest } from "./schema/print-test";
import { printToteBarcodes } from "./schema/print-tote-barcodes";
import { deletePrintProfile } from "./schema/printing/delete-print-profile";
import { listPrintProfiles } from "./schema/printing/list-print-profiles";
import { savePrintProfile } from "./schema/printing/save-print-profile";
import { addSortingTotes } from "./schema/processing-groups/add-sorting-totes";
import { createProcessingGroup } from "./schema/processing-groups/create-processing-group";
import { deleteProcessingGroup } from "./schema/processing-groups/delete-processing-group";
import { listProcessingGroups } from "./schema/processing-groups/list-processing-groups";
import { printSortingToteBarcodes } from "./schema/processing-groups/print-sorting-tote-barcodes";
import { removeSortingTotes } from "./schema/processing-groups/remove-sorting-totes";
import { setProcessingGroupMerchants } from "./schema/processing-groups/set-processing-group-merchants";
import { updateProcessingGroup } from "./schema/processing-groups/update-processing-group";
import {
  addProductConversionLine,
  createProductConversionSession,
  generateProductConversionBarcodeLabels,
} from "./schema/product-conversion-session";
import { publishAutomationGroup } from "./schema/publish-automation-group";
import { purchaseOverboxLabel } from "./schema/purchase-overbox-label";
import { reassignAllItemsToNewTote } from "./schema/reassign-all-items-to-new-tote";
import { reassignAndMoveFulfillmentGroup } from "./schema/reassign-and-move-fulfillment-group";
import { recalculateInventoryProjection } from "./schema/recalculate-inventory-projection";
import { recordAdHocBinPick } from "./schema/record-ad-hoc-bin-pick";
import { recordDirectBinPick } from "./schema/record-direct-bin-pick";
import { recordDirectBinPicks } from "./schema/record-direct-bin-picks";
import { refreshPrintNodeIntegration } from "./schema/refresh-print-node-integration";
import { refreshShippingRates } from "./schema/refresh-shipping-rates";
import { refreshTeamRates } from "./schema/refresh-team-rates";
import { rejectMergeSuggestion } from "./schema/reject-merge-suggestion";
import { releaseAllHolds } from "./schema/release-all-holds";
import { releaseTotesFromFulfillmentGroups } from "./schema/release-totes-from-fulfillment-groups";
import { removeCycleCountProducts } from "./schema/remove-cycle-count-products";
import { renameStorageLocation } from "./schema/rename-storage-location";
import { reorderOmsPriorityFilters } from "./schema/reorder-oms-priority-filters";
import { reorderParcels } from "./schema/reorder-parcels";
import { reorderPickSessionPriorityFilters } from "./schema/reorder-pick-session-priority-filters";
import { reorderWorkOrderPriorityFilters } from "./schema/reorder-work-order-priority-filters";
import { rerunAutomations } from "./schema/rerun-automations";
import { rerunAutoSplit } from "./schema/rerun-autosplit";
import { resetBillingAutoReloadDisabled } from "./schema/reset-billing-auto-reload-disabled";
import { resolvePickLot } from "./schema/resolve-pick-lot";
import { resolveTriage } from "./schema/resolve-triage";
import { retryMarketplaceNotification } from "./schema/retry-marketplace-notification";
import { returnClosedOrderToteInventory } from "./schema/return-closed-order-tote-inventory";
import { saveBulkFulfillmentView } from "./schema/save-bulk-fulfillment-view";
import { saveView } from "./schema/save-view";
import { scanAssignedLicensePlateForPickSession } from "./schema/scan-assigned-license-plate-for-pick-session";
import { searchBoms } from "./schema/search-boms";
import { searchCustomers } from "./schema/search-customers";
import { searchWorkOrdersByScan } from "./schema/search-work-orders-by-scan";
import { sendToTriage } from "./schema/send-to-triage";
import { setDefaultPickSessionFilter } from "./schema/set-default-pick-session-filter";
import { setForceResyncOnGroups } from "./schema/set-force-resync-on-groups";
import { setFreeShippingCodes } from "./schema/set-free-shipping-codes";
import { setFulfillmentGroupPickStatus } from "./schema/set-fulfillment-group-pick-status";
import { setPublishedAutomations } from "./schema/set-published-automations";
import { setStorageLocationPutawayMode } from "./schema/set-storage-location-putaway-mode";
import { setUseOriginalAddress } from "./schema/set-use-original-address";
import { acceptAutoClaimsFiling } from "./schema/shipping-claims/accept-auto-claims-filing";
import {
  getClaimSubmissionSettings,
  recordClaimsFedexLoa,
  updateClaimSubmissionSettings,
} from "./schema/shipping-claims/claim-submission-settings";
import { getMerchantShippingClaim } from "./schema/shipping-claims/get-merchant-shipping-claim";
import { getMerchantShippingClaimSummariesByIds } from "./schema/shipping-claims/get-merchant-shipping-claim-summaries-by-ids";
import { getMerchantShippingClaimUniqueValues } from "./schema/shipping-claims/get-merchant-shipping-claim-unique-values";
import { getMerchantShippingClaims } from "./schema/shipping-claims/get-merchant-shipping-claims";
import { getMerchantShippingClaimsCount } from "./schema/shipping-claims/get-merchant-shipping-claims-count";
import { getProactiveClaimsStats } from "./schema/shipping-claims/get-proactive-claims-stats";
import { submitAdditionalShippingClaimPhotos } from "./schema/shipping-claims/submit-additional-photos";
import { submitShippingClaim } from "./schema/shipping-claims/submit-shipping-claim";
import { updateMerchantShippingClaims } from "./schema/shipping-claims/update-merchant-shipping-claims";
import { skipPickSessionItem } from "./schema/skip-pick-session-item";
import { createSortDestination } from "./schema/sort-destinations/create-sort-destination";
import { deleteSortDestination } from "./schema/sort-destinations/delete-sort-destination";
import { getSortDestinationCoverage } from "./schema/sort-destinations/get-sort-destination-coverage";
import { listSortDestinations } from "./schema/sort-destinations/list-sort-destinations";
import { printSortDestinationBarcodes } from "./schema/sort-destinations/print-sort-destination-barcodes";
import { reorderSortDestinations } from "./schema/sort-destinations/reorder-sort-destinations";
import { updateSortDestination } from "./schema/sort-destinations/update-sort-destination";
import { bulkCreateSortingTotes } from "./schema/sorting-totes/bulk-create-sorting-totes";
import { deleteSortingTotes } from "./schema/sorting-totes/delete-sorting-totes";
import { listSortingTotes } from "./schema/sorting-totes/list-sorting-totes";
import { updateSortingTote } from "./schema/sorting-totes/update-sorting-tote";
import { splitFulfillmentGroup } from "./schema/split-fulfillment-group";
import { splitWorkOrder } from "./schema/split-work-order";
import { startBalanceReload } from "./schema/start-balance-reload";
import { startShipStationApiSync } from "./schema/start-shipstation-api-sync";
import { startWorkOrder } from "./schema/start-work-order";
import { appendSurchargeDisputeAttachments } from "./schema/surcharge-disputes/append-surcharge-dispute-attachments";
import { createSurchargeDispute } from "./schema/surcharge-disputes/create-surcharge-dispute";
import { getRelevantSurchargeData } from "./schema/surcharge-disputes/get-relevant-surcharge-data";
import { getSurchargeDisputes } from "./schema/surcharge-disputes/get-surcharge-disputes";
import { transferItemToBin } from "./schema/transfer-item-to-bin";
import { undeclareWasteWorkOrder } from "./schema/undeclare-waste-work-order";
import { unmergeFulfillmentGroups } from "./schema/unmerge-fulfillment-groups";
import { unmergeFulfillmentOrdersFromGroups } from "./schema/unmerge-fulfillment-orders-from-groups";
import { updateCommentsoldVariantBin } from "./schema/update-commentsold-variant-bin";
import { setFulfillmentGroupPickedQuantities } from "./schema/update-fulfillment-group-pick-status";
import { updateFulfillmentGroups } from "./schema/update-fulfillment-groups";
import { updateFulfillmentPromiseDays } from "./schema/update-fulfillment-promise-days";
import { updateOmsPriorityFilter } from "./schema/update-oms-priority-filter";
import { updateParcel } from "./schema/update-parcel";
import { updatePickSessionFilter } from "./schema/update-pick-session-filter";
import { updatePickSessionItemQuantity } from "./schema/update-pick-session-item-quantity";
import { updatePickSessionPriorityFilter } from "./schema/update-pick-session-priority-filter";
import { updateReturnAddress } from "./schema/update-return-address";
import { updateShippingAddress } from "./schema/update-shipping-address";
import { updateShippingMethod } from "./schema/update-shipping-method";
import { updateVariantBinMetafield } from "./schema/update-variant-bin-metafield";
import { updateWorkOrderPriorityFilter } from "./schema/update-work-order-priority-filter";
import { updateWorkOrderStation } from "./schema/update-work-order-station";
import { upsertParcels } from "./schema/upsert-parcels";
import { createConsolidationViewFolder } from "./schema/views/create-consolidation-view-folder";
import { createReplenishmentRequestViewFolder } from "./schema/views/create-replenishment-request-view-folder";
import { createViewFolder } from "./schema/views/create-view-folder";
import { createWorkOrderViewFolder } from "./schema/views/create-work-order-view-folder";
import { deleteConsolidationView } from "./schema/views/delete-consolidation-view";
import { deleteConsolidationViewFolder } from "./schema/views/delete-consolidation-view-folder";
import { deleteReplenishmentReportView } from "./schema/views/delete-replenishment-report-view";
import { deleteReplenishmentRequestView } from "./schema/views/delete-replenishment-request-view";
import { deleteReplenishmentRequestViewFolder } from "./schema/views/delete-replenishment-request-view-folder";
import { deleteViewFolder } from "./schema/views/delete-view-folder";
import { deleteWorkOrderView } from "./schema/views/delete-work-order-view";
import { deleteWorkOrderViewFolder } from "./schema/views/delete-work-order-view-folder";
import { getConsolidationViewCounts } from "./schema/views/get-consolidation-view-counts";
import { getConsolidationViewsAndFolders } from "./schema/views/get-consolidation-views-and-folders";
import { getInventoryLevelLedgerView } from "./schema/views/get-inventory-level-ledger-view";
import { getProductivityDashboardView } from "./schema/views/get-productivity-dashboard-view";
import { getReplenishmentReportViewCounts } from "./schema/views/get-replenishment-report-view-counts";
import { getReplenishmentReportViews } from "./schema/views/get-replenishment-report-views";
import { getReplenishmentRequestViewCounts } from "./schema/views/get-replenishment-request-view-counts";
import { getReplenishmentRequestViewsAndFolders } from "./schema/views/get-replenishment-request-views-and-folders";
import { getViewsAndFolders } from "./schema/views/get-views-and-folders";
import { getWorkOrderViewCounts } from "./schema/views/get-work-order-view-counts";
import { getWorkOrderViewsAndFolders } from "./schema/views/get-work-order-views-and-folders";
import { saveConsolidationView } from "./schema/views/save-consolidation-view";
import { saveConsolidationViewsAndFolders } from "./schema/views/save-consolidation-views-and-folders";
import { saveInventoryLevelLedgerView } from "./schema/views/save-inventory-level-ledger-view";
import { saveProductivityDashboardView } from "./schema/views/save-productivity-dashboard-view";
import { saveReplenishmentReportView } from "./schema/views/save-replenishment-report-view";
import { saveReplenishmentRequestView } from "./schema/views/save-replenishment-request-view";
import { saveReplenishmentRequestViewsAndFolders } from "./schema/views/save-replenishment-request-views-and-folders";
import { saveViewsAndFolders } from "./schema/views/save-views-and-folders";
import { saveWorkOrderView } from "./schema/views/save-work-order-view";
import { saveWorkOrderViewsAndFolders } from "./schema/views/save-work-order-views-and-folders";
import { updateConsolidationViewFolder } from "./schema/views/update-consolidation-view-folder";
import { updateReplenishmentRequestViewFolder } from "./schema/views/update-replenishment-request-view-folder";
import { updateViewFolder } from "./schema/views/update-view-folder";
import { updateWorkOrderViewFolder } from "./schema/views/update-work-order-view-folder";
import { watchTeamViewCounts } from "./schema/views/watch-team-view-counts";
import { voidShipments } from "./schema/void-shipments";
import { wasteWorkOrder } from "./schema/waste-work-order";
import { watchFulfillmentGroups } from "./schema/watch-fulfillment-groups";
import { watchPrintQueue } from "./schema/watch-print-queue";
import { watchShipStationApiSync } from "./schema/watch-shipstation-api-sync";
import { watchViewExportProgress } from "./schema/watch-view-export-progress";
import { watchWorkstationStatus } from "./schema/watch-workstation-status";
// personalized slips
import { createPersonalizedSlipTemplate } from "./schema/personalized-slips/create-personalized-slip-template";
import { deletePersonalizedSlipTemplate } from "./schema/personalized-slips/delete-personalized-slip-template";
import { generatePersonalizedSlipPreview } from "./schema/personalized-slips/generate-personalized-slip-preview";
import { generateProductRecommendations } from "./schema/personalized-slips/generate-product-recommendations";
import { getPersonalizedSlipTemplate } from "./schema/personalized-slips/get-personalized-slip-template";
import { getPersonalizedSlipTemplatesByTeam } from "./schema/personalized-slips/get-personalized-slip-templates-by-team";
import { updatePersonalizedSlipTemplate } from "./schema/personalized-slips/update-personalized-slip-template";
// warehouse layouts
import { detectDivergentCheckoutParcelPaths } from "./schema/automations/detect-divergent-checkout-parcel-paths";
import { setOmsAutomationEnabled } from "./schema/automations/set-oms-automation-enabled";
import { bulkCreateTotes } from "./schema/bulk-create-totes";
import { countPrintJobs } from "./schema/count-print-jobs";
import { createToteGroup } from "./schema/create-tote-group";
import { createTotes } from "./schema/create-totes";
import { deleteToteGroup } from "./schema/delete-tote-group";
import { duplicateToteGroup } from "./schema/duplicate-tote-group";
import { getAllTeamTotes } from "./schema/get-all-team-totes";
import { getLotDetail } from "./schema/get-lot-detail";
import { getNewToteBarcodes } from "./schema/get-new-tote-barcodes";
import { getPickSessionByToteBarcode } from "./schema/get-pick-session-by-tote-barcode";
import {
  getPlannedPickSessionLots,
  setPickSessionLotOverride,
} from "./schema/get-planned-pick-session-lots";
import { getRecommendedSkus } from "./schema/get-recommended-skus";
import { getToteByBarcode } from "./schema/get-tote-by-barcode";
import { getTotes } from "./schema/get-totes";
import { getTotesByFulfillmentGroupId } from "./schema/get-totes-by-fulfillment-group-id";
import { getTotesByIds } from "./schema/get-totes-by-ids";
import { getVoidReplacementLots } from "./schema/get-void-replacement-lots";
import { modifyShippingMethods } from "./schema/modify-shipping-methods";
import { replacePickSessionTotes } from "./schema/replace-pick-session-totes";
import {
  cancelReplenishmentFromReport,
  cancelReplenishmentRequests,
  claimSourcedReplenishment,
  createReplenishmentRequest,
  createReplenishmentRoute,
  createReplenishmentRouteFromRequests,
  dismissReplenishmentRequest,
  failReplenishmentRequest,
  getReplenishmentDashboard,
  getReplenishmentReportCount,
  getReplenishmentRequestDistinctBinNames,
  getReplenishmentRequestDistinctSkus,
  getReplenishmentRequestsCount,
  getUnsourcedReplenishmentProducts,
  listInProgressSourcingSessions,
  listReplenishmentRequests,
  listSourcedReplenishments,
  reduceReplenishmentPick,
  releaseReplenishmentSession,
  removeReplenishmentProduct,
  resumeReplenishmentFromReport,
  searchReplenishmentReport,
  searchReplenishmentRequests,
  specifyReplenishmentSource,
  startReplenishmentFromReport,
} from "./schema/replenishment";
import { searchPickSessions } from "./schema/search-pick-sessions";
import { searchPickSessionsCount } from "./schema/search-pick-sessions-count";
import { searchPrintJobs } from "./schema/search-print-jobs";
import { searchProductsAtLocation } from "./schema/search-products-at-location";
import { searchReceivableShipments } from "./schema/search-receivable-shipments";
import { searchViewExports } from "./schema/search-view-exports";
import { searchViewExportsCount } from "./schema/search-view-exports-count";
import {
  cancelTransferSession,
  createTransferSession,
  getTransferSessionState,
  returnTransferSessionItemToSource,
  skipTransferSessionItem,
  updateTransferSession,
} from "./schema/transfer-session";
import { updateTote } from "./schema/update-tote";
import { updateToteGroup } from "./schema/update-tote-group";
import { checkWarehouseLayoutInventoryConflicts } from "./schema/warehouse-layouts/check-warehouse-layout-inventory-conflicts";
import { checkWarehouseLayoutVersion } from "./schema/warehouse-layouts/check-warehouse-layout-version";
import { clearRemovedWarehouseBinInventory } from "./schema/warehouse-layouts/clear-removed-warehouse-bin-inventory";
import { deleteWarehouseLayout } from "./schema/warehouse-layouts/delete-warehouse-layout";
import { getWarehouseLayout } from "./schema/warehouse-layouts/get-warehouse-layout";
import { getWarehouseLayoutForLocation } from "./schema/warehouse-layouts/get-warehouse-layout-for-location";
import { listWarehouseLayouts } from "./schema/warehouse-layouts/list-warehouse-layouts";
import { upsertWarehouseLayout } from "./schema/warehouse-layouts/upsert-warehouse-layout";
import { watchOmsSettings } from "./schema/watch-oms-settings";
import { watchPrinters } from "./schema/watch-printers";
// custom order import schemas
import { deleteCustomImportSchema } from "./schema/import/delete-custom-import-schema";
import { getCustomImportSchemas } from "./schema/import/get-custom-import-schemas";
import { saveCustomImportSchema } from "./schema/import/save-custom-import-schema";
import { suggestImportSchemaMapping } from "./schema/import/suggest-import-schema-mapping";

// cross-border inclusive-pricing markets
import { deleteMarket } from "./schema/markets/delete-market";
import { getMarkets } from "./schema/markets/get-markets";
import { getProductFamilyRestrictions } from "./schema/markets/get-product-family-restrictions";
import { getProductRestrictions } from "./schema/markets/get-product-restrictions";
import { getShopifyMarkets } from "./schema/markets/get-shopify-markets";
import { listAccountRestrictionMeasures } from "./schema/markets/list-account-restriction-measures";
import {
  resolveRestrictionMeasure,
  unresolveRestrictionMeasure,
} from "./schema/markets/resolve-restriction-measure";
import { setProductFamilyPublishOverride } from "./schema/markets/set-product-family-publish-override";
import { setProductPublishOverride } from "./schema/markets/set-product-publish-override";
import { upsertMarket } from "./schema/markets/upsert-market";

// -----------------------------------------------------------------------------
// RPC Definition Groups
// Splitting into smaller groups improves TypeScript declaration emit performance.
// When InferRpcDefinition processes 250+ endpoints at once, TypeScript must
// compute and serialize massive intersection types. By pre-computing smaller
// group types and combining them, we reduce emit time significantly.
// See: https://github.com/microsoft/TypeScript/wiki/Performance#preferring-interfaces-over-intersections
// -----------------------------------------------------------------------------

const coreEndpoints = {
  acceptFedexAgreement,
  acceptMerchantCarrierFeeTerms,
  acceptPersonalizedSlipsTerms,
  acceptUpsAgreement,
  moveFulfillmentGroupToTote,
  moveFulfillmentGroupToTotes,
  setFulfillmentGroupPickStatus,
  setFulfillmentGroupPickedQuantities,
  assignAllUnassignedItemsToTote,
  assignItemToTote,
  pickAndAssignItemToTote,
  replacePickSessionTotes,
  reassignAllItemsToNewTote,
  markToteAsFull,
} as const;

const automationEndpoints = {
  publishAutomationGroup,
  rerunAutomations,
  rerunAutoSplit,
  setPublishedAutomations,
  detectDivergentCheckoutParcelPaths,
  setOmsAutomationEnabled,
} as const;

const claimsEndpoints = {
  submitShippingClaim,
  submitAdditionalShippingClaimPhotos,
  updateMerchantShippingClaims,
  getMerchantShippingClaim,
  getMerchantShippingClaims,
  getMerchantShippingClaimsCount,
  getMerchantShippingClaimUniqueValues,
  getMerchantShippingClaimSummariesByIds,
  getProactiveClaimsStats,
  acceptAutoClaimsFiling,
  getClaimSubmissionSettings,
  updateClaimSubmissionSettings,
  recordClaimsFedexLoa,
} as const;

const fulfillmentEndpoints = {
  acknowledgeBuyLabelErrors,
  createManualFulfillmentGroups,
  cancelFulfillmentGroups,
  cancelManualFulfillments,
  cancelOrderItems,
  cancelFreightBooking,
  fulfillFulfillmentGroupWithFreightBooking,
  cancelPicking,
  cancelPickSession,
  completePickSessionSourcing,
  markPickSessionAsPicked,
  countPrintJobs,
  getAllTeamTotes,
  getTotes,
  getTotesByIds,
  deleteTotes,
  createTotes,
  bulkCreateTotes,
  createToteGroup,
  deleteToteGroup,
  duplicateToteGroup,
  updateTote,
  updateToteGroup,
  getToteByBarcode,
  getTotesByFulfillmentGroupId,
  getPickSessionByToteBarcode,
  getVoidReplacementLots,
  getLabelPrebought,
  getPlannedPickSessionLots,
  setPickSessionLotOverride,
  getLotDetail,
  getNewToteBarcodes,
  editManualFulfillmentGroups,
  getAddressesFromName,
  getFulfillmentGroupsForOrder,
  getFulfillmentGroupsByOrderId,
  getFulfillmentGroupSummaries,
  getShippedItemsReportData,
  getFulfillmentGroupSummaryCount,
  getFulfillmentGroupUniqueValues,
  getOptimizedBatchFulfillmentGroupIds,
  getFulfillmentServiceForTeam,
  getFulfillmentServicesThatFulfillForTeam,
  getLatestOpenFulfillmentGroup,
  getNonPickedProductIds,
  getPossibleReturnsForProblemOrder,
  uploadParcelPhoto,
  deleteParcelPhoto,
  getParcelPhotos,
  recordParcelPhotoBypass,
  createPickSession,
  createConsolidationPackingCarton,
  setConsolidationCartonParcel,
  getActiveUserPickSessions,
  getConsolidationPackingCartonLabel,
  getConsolidationCarton,
  getConsolidationCartons,
  prepareConsolidationCartonLabel,
  quoteConsolidationCartonLabel,
  saveConsolidationCartonLabelDraft,
  getConsolidationCartonReturnsCenters,
  printConsolidationCartonShippingLabel,
  purchaseConsolidationCartonLabel,
  getConsolidationMerchantPackingSession,
  getConsolidationPackingSummary,
  getPickSession,
  listAvailableHandoffsAtZone,
  getHandoffByToteBarcode,
  previewToteHandoffs,
  getFulfillmentGroupMerchantTeamIds,
  getTransferLineMerchantTeamIds,
  getPickSessionRoute,
  searchPickSessions,
  searchPickSessionsCount,
  searchPrintJobs,
  searchViewExports,
  searchViewExportsCount,
  moveConsolidationPackingUnit,
  packConsolidationInventoryItem,
  putAwayInventoryItemIntoConsolidationCarton,
  removeConsolidationPackingCarton,
  sealConsolidationPacking,
  setConsolidationPackingPlan,
  unpackConsolidationPackingUnit,
  updatePickSessionItemQuantity,
  scanAssignedLicensePlateForPickSession,
  markPickSessionItemOutOfStock,
  markPickSessionItemInStock,
  skipPickSessionItem,
  changePickSessionItemBin,
  createPickSessionFilter,
  updatePickSessionFilter,
  deletePickSessionFilter,
  setDefaultPickSessionFilter,
  getPickSessionFillCandidates,
  getPickSessionFilters,
  createPickSessionPriorityFilter,
  updatePickSessionPriorityFilter,
  deletePickSessionPriorityFilter,
  getPickSessionPriorityFilters,
  reorderPickSessionPriorityFilters,
  createOmsPriorityFilter,
  updateOmsPriorityFilter,
  deleteOmsPriorityFilter,
  getOmsPriorityFilters,
  reorderOmsPriorityFilters,
  createWorkOrderPriorityFilter,
  updateWorkOrderPriorityFilter,
  deleteWorkOrderPriorityFilter,
  getWorkOrderPriorityFilters,
  reorderWorkOrderPriorityFilters,
  getAutoMergeProgress,
  getFulfillmentBacksyncProgress,
  recalculateInventoryProjection,
  getRefreshTeamRatesProgress,
  getRelevantFulfillmentGroupData,
  getReplacementGroups,
  getRerunAutomationsProgress,
  markAsFulfilledWithData,
  markAsPickedUp,
  markAsPrinted,
  markAsReadyForPickup,
  markFulfillmentGroupAsProblem,
  markFulfillmentGroupsAsProblemPick,
  notifyMarketplace,
  retryMarketplaceNotification,
  overrideNotReceivedGroups,
  placeHolds,
  releaseAllHolds,
  releaseTotesFromFulfillmentGroups,
  splitFulfillmentGroup,
  updateFulfillmentGroups,
  watchFulfillmentGroups,
  watchOmsSettings,
  getPrintQueueStatus,
  watchPrintQueue,
  watchPrinters,
  watchViewExportProgress,
  watchWorkstationStatus,
} as const;

const invoiceEndpoints = { getInvoiceCsv };

const labelEndpoints = {
  buyReturnLabel,
  buyBulkShipmentLabels,
  buyLabels,
  checkPrintLabelInventory,
  buyPriorityFilterLabels,
  cancelPrintJobs,
  dismissPrintJobs,
  refreshPrintNodeIntegration,
  createReturnLabel,
  deletePrintProfile,
  listPrintProfiles,
  savePrintProfile,
  getOmsReturnLabelRates,
  enqueuePrintJobs,
  printBarcodeForProduct,
  printBillOfLading,
  printCommercialInvoices,
  printLabels,
  printLocalPickUpLabels,
  printReturnLabels,
  printPackingSlip,
  printOrderSummaryReport,
  printParcelPackingSlips,
  printPersonalizedSlip,
  printPickList,
  printPickSessions,
  printShortcutBarcodes,
  printTest,
  printBinLabels,
  printStorageLocationLabels,
  printLocationLabels,
  printToteBarcodes,
  logPrintQueue,
  logPrintJobProcessing,
} as const;

const manifestEndpoints = {
  createEodManifestForAll,
  createEodManifestForIds,
  getEndOfDayManifests,
  getManifestableGroupsCounts,
} as const;

const mergeEndpoints = {
  autoMergeSpecifiedGroups,
  getMergeSuggestionGroups,
  getMergeSuggestions,
  getMergeSuggestionsForGroup,
  getMergeSuggestionSummaries,
  mergeFulfillmentGroups,
  rejectMergeSuggestion,
  enableAutoMerge,
  unmergeFulfillmentGroups,
  unmergeFulfillmentOrdersFromGroups,
} as const;

const otherEndpoints = {
  completeShipmentReceiving,
  getUnplacedShipmentReceipt,
  createPrintNodeAccount,
  createTaxIdentifier,
  createTransferSession,
  deleteTaxIdentifiers,
  createBalanceTransactionExport,
  createInventoryLevelLedgerExport,
  executeInstantTransfer,
  exportEnrichedBalanceTransactions,
  getBalanceTransactionData,
  getBalanceTransactionCount,
  getSurchargeDisputes,
  createSurchargeDispute,
  appendSurchargeDisputeAttachments,
  getRelevantSurchargeData,
  getBatchHistory,
  getBatchHistoryCount,
  getBadWarehouseBins,
  getOmsAutoMergeOrderExamples,
  getOmsAutoMergeSavings,
  addOutboundFunds,
  getOutboundBalance,
  getOutboundInvoice,
  getOutboundInvoices,
  getOutboundLabelsMetrics,
  getPackingSlipPredictions,
  getPackingSlipWeatherWarnings,
  getPointsTransactions,
  getPointsTransactionsCount,
  getCurrentPoints,
  getPrintNodeInfo,
  dismissPrintEngineRecommendation,
  getProductivityDashboardData,
  getTaxIdentifiers,
  getFreeShippingCodes,
  setFreeShippingCodes,
  getMintsoftCourierConnection,
  setMintsoftCourierConnection,
  getCarrierServiceCombinations,
  getTeamCarrierServiceOptions,
  getUpsGhostAccountIntegration,
  createShipStationApiIntegration,
  getShipStationApiIntegration,
  startShipStationApiSync,
  watchShipStationApiSync,
  hasCarrierServices,
  getPickSessionLotRequirements,
  hasLotTrackedProducts,
  healPickSessionSourcingDrift,
  lookupOrder,
  lookupProductByBarcode,
  lookupWorkOrderByInventoryItem,
  transferItemToBin,
  signInPrintNodeAccount,
  resetBillingAutoReloadDisabled,
  startBalanceReload,
  updatePrintNodePassword,
  getActiveUserTransferSessions,
  getTransferSessionState,
  skipTransferSessionItem,
  returnTransferSessionItemToSource,
  cancelTransferSession,
  updateTransferSession,
  getBatchSuggestions,
  getBatchSuggestionByKey,
  getBatchSuggestionsByKeys,
  getBulkOneClickPrintFulfillmentGroupIds,
  getBulkOneClickPrintSummary,
  getPickListCsv,
  createStorageLocations,
  deleteStorageLocations,
  getCrdbLocations,
  getEligibleBinCount,
  getOnboardingProgress,
  listAssignedCycleCounts,
  getBinInventory,
  addProductToStorageLocation,
  adjustInventoryLevel,
  confirmDirectBinCount,
  dissociateStorageLocationInventory,
  setStorageLocationPutawayMode,
  renameStorageLocation,
  getInventoryLevelLedger,
  getInventoryLevelLedgerCount,
  getInventoryLevelLedgerUniqueValues,
  getStorageLocations,
  getStorageLocationsCount,
  advanceWorkOrder,
  assignWorkOrderTote,
  bulkAssignWorkOrderTotes,
  bulkPickWorkOrderLines,
  cancelWorkOrder,
  wasteWorkOrder,
  sendToTriage,
  resolveTriage,
  resolvePickLot,
  undeclareWasteWorkOrder,
  generateWorkOrderBarcodeLabels,
  generateBomInputDemand,
  generateWorkOrderGenerationReport,
  createWorkOrder,
  declarePlacingLost,
  getBoms,
  searchBoms,
  searchCustomers,
  getPlacingBatchesByTote,
  getPlacingWorkOrdersByTote,
  getWorkOrder,
  getWorkOrderBatch,
  getWorkOrders,
  getWorkOrdersCount,
  getWorkOrderStepTitles,
  getWorkOrderBomNames,
  getWorkOrderInputProductNames,
  getWorkOrderBomSkus,
  mergeWorkOrders,
  pickWorkOrderLine,
  previewWorkOrder,
  splitWorkOrder,
  startWorkOrder,
  createWorkOrderStation,
  updateWorkOrderStation,
  deleteWorkOrderStation,
  getWorkOrderStations,
  getWorkOrderStationV2Enabled,
  getStationQueue,
  getSiblingBatches,
  createBulkPickSession,
  getActiveBulkPickSession,
  cancelBulkPickSession,
  completeBulkPickSession,
  releaseBulkPickSession,
  takeOverBulkPickSession,
  createPriorityBomPickSession,
  claimWorkOrderStep,
  markUnableToPick,
  bulkAdvancePlacing,
  bulkMarkUnableToPick,
  bulkAdvanceWorkOrders,
  getBulkPickLines,
  getPickLinesForWorkOrders,
  lookupWorkOrdersByTote,
  searchWorkOrdersByScan,
  getBinsForProduct,
  getBinsForProducts,
  getBinsForProductFamilyVariants,
  getClosedOrderToteReturn,
  getScanVerifyPopups,
  returnClosedOrderToteInventory,
  getSuggestedTransferDestinations,
  getSuggestedBinsForFulfillmentGroup,
  getProductFamilyVariants,
  getProductInventoryInBin,
  getAvailableBinsForFulfillmentGroups,
  getAvailableBinsForUnpickedItems,
  recordAdHocBinPick,
  recordDirectBinPick,
  recordDirectBinPicks,
  fulfillBinsAsManualGroup,
  getBinInventoryForFulfillment,
  getTransferGuidance,
  getStorageLocationById,
  getStorageLocationByName,
  getStorageLocationPrefixGroup,
  getStorageLocationsForTransfer,
  getStorageLocationFilterLocations,
  getStorageLocationUniqueValues,
  searchProductsAtLocation,
  getGraphicBlanks,
  getRecommendedSkus,
  searchReceivableShipments,
  listReceivableShipments,
} as const;

const parcelEndpoints = {
  createParcel,
  createParcels,
  deleteParcel,
  getCarrierParcels,
  getParcelAutomationReferences,
  getParcels,
  reorderParcels,
  updateParcel,
  upsertParcels,
} as const;

const presetEndpoints = {
  createPreset,
  deletePreset,
  getPresets,
  updatePreset,
} as const;

const merchantDashboardEndpoints = {
  addMerchantDashboardWidget,
  createMerchantDashboard,
  deleteMerchantDashboard,
  getMerchantDashboard,
  getOrCreateDefaultMerchantDashboard,
  listMerchantDashboards,
  removeMerchantDashboardWidget,
  reorderMerchantDashboards,
  updateMerchantDashboard,
  updateMerchantDashboardWidget,
} as const;

const processingGroupEndpoints = {
  addSortingTotes,
  createProcessingGroup,
  deleteProcessingGroup,
  listProcessingGroups,
  printSortingToteBarcodes,
  removeSortingTotes,
  setProcessingGroupMerchants,
  updateProcessingGroup,
} as const;

const sortDestinationEndpoints = {
  createSortDestination,
  deleteSortDestination,
  getSortDestinationCoverage,
  listSortDestinations,
  printSortDestinationBarcodes,
  reorderSortDestinations,
  updateSortDestination,
} as const;

const sortingToteEndpoints = {
  bulkCreateSortingTotes,
  deleteSortingTotes,
  listSortingTotes,
  updateSortingTote,
} as const;

const shippingEndpoints = {
  createDefaultBucketShippingMethods,
  createReturnAddress,
  createShippingMethod,
  deleteShippingMethod,
  duplicateShipments,
  getFulfillmentGroupShoppedServices,
  getOriginAddresses,
  getReturnAddresses,
  modifyShippingMethods,
  moveFulfillmentGroups,
  getReassignmentLocations,
  assignFulfillmentGroupToMerchant,
  reassignAndMoveFulfillmentGroup,
  updateReturnAddress,
  updateShippingAddress,
  setUseOriginalAddress,
  updateShippingMethod,
  updateCommentsoldVariantBin,
  updateVariantBinMetafield,
  getProductsWithoutBinMetafield,
  voidShipments,
  refreshShippingRates,
  refreshTeamRates,
} as const;

const viewEndpoints = {
  createViewExport,
  createViewFolder,
  createReplenishmentRequestViewFolder,
  createWorkOrderViewFolder,
  deleteFulfillmentGroupView,
  deleteReplenishmentRequestView,
  deleteReplenishmentRequestViewFolder,
  deleteViewFolder,
  deleteWorkOrderView,
  deleteWorkOrderViewFolder,
  getBulkFulfillmentView,
  getInventoryLevelLedgerView,
  getProductivityDashboardView,
  getReplenishmentRequestViewCounts,
  getReplenishmentRequestViewsAndFolders,
  getViewsAndFolders,
  getWorkOrderViewCounts,
  getWorkOrderViewsAndFolders,
  saveBulkFulfillmentView,
  saveInventoryLevelLedgerView,
  saveProductivityDashboardView,
  deleteReplenishmentReportView,
  getReplenishmentReportViewCounts,
  getReplenishmentReportViews,
  saveReplenishmentReportView,
  deleteConsolidationView,
  getConsolidationViewCounts,
  getConsolidationViewsAndFolders,
  createConsolidationViewFolder,
  deleteConsolidationViewFolder,
  saveConsolidationViewsAndFolders,
  updateConsolidationViewFolder,
  saveConsolidationView,
  saveReplenishmentRequestView,
  saveReplenishmentRequestViewsAndFolders,
  saveView,
  saveViewsAndFolders,
  saveWorkOrderView,
  saveWorkOrderViewsAndFolders,
  updateReplenishmentRequestViewFolder,
  updateViewFolder,
  updateWorkOrderViewFolder,
  watchTeamViewCounts,
} as const;

const dashboardEndpoints = {
  getAverageFulfillmentPerHour,
  getAddressAutoUpdatedCount,
  getAverageLabelPrice,
  getAvgAdjustmentByCarrier,
  getAvgLabelCostByCarrier,
  getAvgTimeToDeliverByCarrier,
  getAvgTimeToDeliverByServiceLevel,
  getCostPerOunceShipped,
  getLostOrdersCount,
  getMergedOrderSavings,
  getOmsInsights,
  getOnTimeDeliveryRate,
  getOnTimeFulfillmentRate,
  getEventTypeCount,
  getOrderToShipTime,
  getPackingErrorRate,
  getPickToPackTime,
  getDashboardSlaViews,
  saveDashboardSlaView,
  resetDashboardSlaView,
  getSplitOrderCount,
  getSplitOrderRate,
  getShipmentErrors,
  getShipmentVolumeByPackage,
  getShipmentsByCarrier,
  getSpendByCarrier,
  getTotalAdjustmentByCarrier,
  getCarrierVolumeOverTime,
  getServiceLevelVolumeOverTime,
  getServiceLevelShippingMarginOverTime,
  getShippingMarginOverTime,
  getCarrierSpendOverTime,
  getCarrierSurchargesOverTime,
  getFulfillmentsOverTime,
  getAvgDaysToFulfillOverTime,
  getTotalLabelCost,
  getTotalProductsShipped,
  getTotalRevenueShipped,
  getTotalShipments,
  getWorkOrderItemsCompleted,
  getWorkOrderItemsCompletedByProduct,
  getUniqueCarrierCount,
  getAvgShippingRevenuePerOrder,
  getAvgSurcharge,
  getSurchargeByCategory,
  updateDashboardMetricsSettings,
  updateFulfillmentPromiseDays,
  getOrdersMetrics,
  getTotalOrdersOverTime,
  getSalesVsRefundsOverTime,
  getOrdersByChannel,
  getAovByChannel,
  getFulfillmentsMetrics,
  getErrorFulfillmentBreakdown,
  getVolumeByLocation,
  getShippingMetrics,
  getCarrierDeliveryErrorsOverTime,
  getTopShippingErrors,
  getShippingErrorRateByCarrier,
  getAvgClaimValueByCarrier,
  getAvgTimeToResolutionByCarrier,
  getClaimApprovalRateOverTimeByCarrier,
  getClaimsByCarrier,
  getClaimsByReason,
  getClaimsMetrics,
  getClaimsRevenueRecoveredOverTime,
  getTopDenialReasons,
  getCanonicalLocations,
} as const;

const personalizedSlipsEndpoints = {
  createPersonalizedSlipTemplate,
  deletePersonalizedSlipTemplate,
  generatePersonalizedSlipPreview,
  generateProductRecommendations,
  getPersonalizedSlipTemplate,
  getPersonalizedSlipTemplatesByTeam,
  updatePersonalizedSlipTemplate,
} as const;

const warehouseLayoutsEndpoints = {
  checkWarehouseLayoutInventoryConflicts,
  checkWarehouseLayoutVersion,
  clearRemovedWarehouseBinInventory,
  upsertWarehouseLayout,
  deleteWarehouseLayout,
  getWarehouseLayout,
  getWarehouseLayoutForLocation,
  listWarehouseLayouts,
} as const;

const forceResyncEndpoints = { setForceResyncOnGroups };

const allowedProblemPickPolicyEndpoints = {
  getAllowedProblemPickPolicies,
  upsertAllowedProblemPickPolicy,
  deleteAllowedProblemPickPolicy,
} as const;

const itemWorkRequestEndpoints = {
  createItemWorkRequest,
  searchItemWorkRequests,
  getItemWorkRequestsCount,
  cancelItemWorkRequest,
  rejectItemWorkRequest,
  listPhotoInstructions,
  savePhotoInstructions,
  deletePhotoInstructions,
  getItemPhotoRequestForItem,
  uploadItemPhotoCapture,
  deleteItemPhotoCapture,
  completeItemWorkRequest,
  getItemWorkRequestPhotos,
} as const;

const lotsEndpoints = {
  listLots,
  createLot,
  updateLot,
  getLots,
  getLotsCount,
  getLotProducts,
} as const;

const replenishmentEndpoints = {
  getReplenishmentDashboard,
  createReplenishmentRoute,
  createReplenishmentRouteFromRequests,
  dismissReplenishmentRequest,
  failReplenishmentRequest,
  reduceReplenishmentPick,
  releaseReplenishmentSession,
  removeReplenishmentProduct,
  listInProgressSourcingSessions,
  listSourcedReplenishments,
  claimSourcedReplenishment,
  specifyReplenishmentSource,
  getUnsourcedReplenishmentProducts,
  listReplenishmentRequests,
  searchReplenishmentRequests,
  getReplenishmentRequestsCount,
  searchReplenishmentReport,
  getReplenishmentReportCount,
  startReplenishmentFromReport,
  cancelReplenishmentFromReport,
  resumeReplenishmentFromReport,
  getReplenishmentRequestDistinctSkus,
  getReplenishmentRequestDistinctBinNames,
  createReplenishmentRequest,
  cancelReplenishmentRequests,
  claimAndStartTask,
  getReplenishmentQueue,
  startNextReplenishmentTask,
  completeCycleCount,
  createCycleCount,
  addCycleCountProducts,
  removeCycleCountProducts,
  deleteCycleCounts,
  listCycleCounts,
  getCycleCount,
} as const;

const binAssignmentEndpoints = {
  createBinAssignmentSession,
  getBinAssignmentSession,
  getActiveBinAssignmentSessions,
  upsertBinAssignmentProduct,
  removeBinAssignmentProduct,
  completeBinAssignmentSession,
  cancelBinAssignmentSession,
} as const;

const productConversionEndpoints = {
  createProductConversionSession,
  addProductConversionLine,
  generateProductConversionBarcodeLabels,
} as const;

const consolidationEndpoints = {
  closeConsolidation,
  createConsolidationBins,
  createConsolidationFulfillmentGroup,
  createConsolidationGroup,
  createConsolidationSchedule,
  createManualConsolidation,
  deleteConsolidationBins,
  deleteConsolidationGroup,
  fulfillConsolidation,
  getConsolidationBinsOnHand,
  getConsolidationCommercialInvoiceCsv,
  getConsolidationCommercialInvoicePdf,
  getConsolidationFilterOptions,
  getConsolidationInventoryItems,
  getConsolidationInvoiceCsv,
  getConsolidationInvoicePdf,
  getConsolidationManifestCsv,
  getConsolidationManifestPdf,
  getConsolidationSettings,
  getConsolidationStatusCounts,
  getManualConsolidationOptions,
  getManagedMerchants,
  getMerchantGradeOptions,
  getParcelBoxLabel,
  getParcelPackingSlip,
  listConsolidationBins,
  listConsolidationDocuments,
  listConsolidationInventory,
  listConsolidations,
  printConsolidationBinLabels,
  sealConsolidation,
  searchMerchantSkus,
  updateConsolidationBinCriteria,
  updateConsolidationGroup,
  updateConsolidationSchedule,
  updateConsolidationScheduleAssignments,
} as const;

const importEndpoints = {
  saveCustomImportSchema,
  getCustomImportSchemas,
  deleteCustomImportSchema,
  suggestImportSchemaMapping,
} as const;

const marketsEndpoints = {
  getMarkets,
  getProductFamilyRestrictions,
  getProductRestrictions,
  getShopifyMarkets,
  upsertMarket,
  deleteMarket,
  listAccountRestrictionMeasures,
  resolveRestrictionMeasure,
  unresolveRestrictionMeasure,
  setProductPublishOverride,
  setProductFamilyPublishOverride,
} as const;

const overboxLabelsEndpoints = {
  getOverboxConfig,
  getOverboxLeg,
  listOverboxLegs,
  purchaseOverboxLabel,
} as const;

// Pre-compute group types to reduce declaration emit complexity
type CoreRpc = InferRpcDefinition<typeof coreEndpoints>;
type AutomationRpc = InferRpcDefinition<typeof automationEndpoints>;
type ClaimsRpc = InferRpcDefinition<typeof claimsEndpoints>;
type FulfillmentRpc = InferRpcDefinition<typeof fulfillmentEndpoints>;
type InvoiceRpc = InferRpcDefinition<typeof invoiceEndpoints>;
type LabelRpc = InferRpcDefinition<typeof labelEndpoints>;
type ManifestRpc = InferRpcDefinition<typeof manifestEndpoints>;
type MergeRpc = InferRpcDefinition<typeof mergeEndpoints>;
type OtherRpc = InferRpcDefinition<typeof otherEndpoints>;
type ParcelRpc = InferRpcDefinition<typeof parcelEndpoints>;
type PresetRpc = InferRpcDefinition<typeof presetEndpoints>;
type ShippingRpc = InferRpcDefinition<typeof shippingEndpoints>;
type ViewRpc = InferRpcDefinition<typeof viewEndpoints>;
type DashboardRpc = InferRpcDefinition<typeof dashboardEndpoints>;
type PersonalizedSlipsRpc = InferRpcDefinition<
  typeof personalizedSlipsEndpoints
>;
type WarehouseLayoutsRpc = InferRpcDefinition<typeof warehouseLayoutsEndpoints>;
type ForceResyncRpc = InferRpcDefinition<typeof forceResyncEndpoints>;
type ReplenishmentRpc = InferRpcDefinition<typeof replenishmentEndpoints>;
type BinAssignmentRpc = InferRpcDefinition<typeof binAssignmentEndpoints>;
type ProductConversionRpc = InferRpcDefinition<
  typeof productConversionEndpoints
>;
type ConsolidationRpc = InferRpcDefinition<typeof consolidationEndpoints>;

type ImportRpc = InferRpcDefinition<typeof importEndpoints>;
type MarketsRpc = InferRpcDefinition<typeof marketsEndpoints>;
type OverboxLabelsRpc = InferRpcDefinition<typeof overboxLabelsEndpoints>;
type AllowedProblemPickPolicyRpc = InferRpcDefinition<
  typeof allowedProblemPickPolicyEndpoints
>;
type LotsRpc = InferRpcDefinition<typeof lotsEndpoints>;
type ItemWorkRequestRpc = InferRpcDefinition<typeof itemWorkRequestEndpoints>;
type MerchantDashboardRpc = InferRpcDefinition<
  typeof merchantDashboardEndpoints
>;
type ProcessingGroupRpc = InferRpcDefinition<typeof processingGroupEndpoints>;
type SortDestinationRpc = InferRpcDefinition<typeof sortDestinationEndpoints>;
type SortingToteRpc = InferRpcDefinition<typeof sortingToteEndpoints>;

export const rpcDefinition: typeof coreEndpoints &
  typeof automationEndpoints &
  typeof claimsEndpoints &
  typeof fulfillmentEndpoints &
  typeof invoiceEndpoints &
  typeof labelEndpoints &
  typeof manifestEndpoints &
  typeof mergeEndpoints &
  typeof otherEndpoints &
  typeof parcelEndpoints &
  typeof presetEndpoints &
  typeof merchantDashboardEndpoints &
  typeof processingGroupEndpoints &
  typeof sortDestinationEndpoints &
  typeof sortingToteEndpoints &
  typeof shippingEndpoints &
  typeof viewEndpoints &
  typeof dashboardEndpoints &
  typeof personalizedSlipsEndpoints &
  typeof warehouseLayoutsEndpoints &
  typeof forceResyncEndpoints &
  typeof replenishmentEndpoints &
  typeof binAssignmentEndpoints &
  typeof productConversionEndpoints &
  typeof consolidationEndpoints &
  typeof importEndpoints &
  typeof marketsEndpoints &
  typeof overboxLabelsEndpoints &
  typeof allowedProblemPickPolicyEndpoints &
  typeof lotsEndpoints &
  typeof itemWorkRequestEndpoints = {
  ...coreEndpoints,
  ...automationEndpoints,
  ...claimsEndpoints,
  ...fulfillmentEndpoints,
  ...invoiceEndpoints,
  ...labelEndpoints,
  ...manifestEndpoints,
  ...mergeEndpoints,
  ...otherEndpoints,
  ...parcelEndpoints,
  ...presetEndpoints,
  ...merchantDashboardEndpoints,
  ...processingGroupEndpoints,
  ...sortDestinationEndpoints,
  ...sortingToteEndpoints,
  ...shippingEndpoints,
  ...viewEndpoints,
  ...dashboardEndpoints,
  ...personalizedSlipsEndpoints,
  ...warehouseLayoutsEndpoints,
  ...forceResyncEndpoints,
  ...replenishmentEndpoints,
  ...binAssignmentEndpoints,
  ...productConversionEndpoints,
  ...consolidationEndpoints,
  ...importEndpoints,
  ...marketsEndpoints,
  ...overboxLabelsEndpoints,
  ...allowedProblemPickPolicyEndpoints,
  ...lotsEndpoints,
  ...itemWorkRequestEndpoints,
};

// Combine pre-computed types instead of inferring from the full object
export type OutboundLabelsRpcDefinition = CoreRpc &
  AutomationRpc &
  ClaimsRpc &
  FulfillmentRpc &
  InvoiceRpc &
  LabelRpc &
  ManifestRpc &
  MergeRpc &
  OtherRpc &
  ParcelRpc &
  PresetRpc &
  MerchantDashboardRpc &
  ProcessingGroupRpc &
  SortDestinationRpc &
  SortingToteRpc &
  ShippingRpc &
  ViewRpc &
  DashboardRpc &
  PersonalizedSlipsRpc &
  WarehouseLayoutsRpc &
  ForceResyncRpc &
  ReplenishmentRpc &
  BinAssignmentRpc &
  ProductConversionRpc &
  ConsolidationRpc &
  ImportRpc &
  MarketsRpc &
  OverboxLabelsRpc &
  AllowedProblemPickPolicyRpc &
  LotsRpc &
  ItemWorkRequestRpc;
